import * as React from 'react'
import getMiniDecimal, {
  getNumberPrecision,
  num2str,
  toFixed,
  trimNumber,
  validateNumber,
} from '@rc-component/mini-decimal'
import type { DecimalClass, ValueType } from '@rc-component/mini-decimal'

/* -------------------------------------------------------------------------
 * The VALUE MACHINE of the shadcn half of the `../InputNumber` seam.
 *
 * Every rule here is a TRANSCRIPTION of rc-input-number 9.0.0 — the version
 * antd 5.15.4 ships and this repo pins — so the shadcn branch parses, clamps,
 * rounds, steps and emits exactly what the antd branch beside it does:
 *
 *   • rc-input-number/es/InputNumber.tsx      -> everything below
 *   • rc-input-number/es/utils/numberUtil.ts  -> getDecupleSteps
 *   • rc-input-number/es/hooks/useCursor.ts   -> useCursor
 *   • rc-input-number/es/hooks/useFrame.ts    -> useFrame
 *
 * The ONE deliberate divergence is the DEFAULT decimal separator, and it does
 * not live here: it is applied by the caller, and `./decimalSeparator.ts` is
 * the file that states why. Everything in this module treats
 * `decimalSeparator` as rc does — as a prop.
 *
 * WHY A TRANSCRIPTION AND NOT A REWRITE. 185 tags were written against these
 * rules: 145 pass `min`, 44 `step`, 32 `precision`. "Clamp on blur but not
 * while typing", "auto-derive precision from `step` unless `precision` is
 * given", "let `1.` stay `1.` mid-typing", "re-clamp after rounding pushed the
 * value back out of range" are not incidental — a call site that stops
 * clamping, or starts rounding a keystroke early, is a behaviour change.
 *
 * WHY `@rc-component/mini-decimal` IS A DIRECT DEPENDENCY NOW. It is the exact
 * decimal arithmetic rc-input-number itself uses, and it is a framework-free
 * numeric library (its only dependency is `@babel/runtime`), NOT an antd or rc
 * UI component — so it survives antd's deletion, which the antd-internal
 * modules would not. Sharing it is what makes the two halves arithmetically
 * IDENTICAL rather than merely similar: `0.1 + 0.2` is `0.3` on both branches,
 * and a money field cannot round one way in one design and the other way in
 * the other. It was already installed as antd's own transitive dependency at
 * this exact version, so adopting it added one line to the lockfile and no
 * bytes to the bundle. PINNED EXACT, like `lucide-react` and `recharts`, and
 * for a sharper reason than either: a silent minor of a rounding library
 * changes numbers.
 * ------------------------------------------------------------------------- */

/** rc-input-number/es/utils/numberUtil.ts — the shift-key decuple step. */
export const getDecupleSteps = (step: ValueType): string => {
  const stepStr =
    typeof step === 'number' ? num2str(step) : trimNumber(step).fullStr
  const hasPoint = stepStr.includes('.')
  if (!hasPoint) return `${step}0`
  return trimNumber(stepStr.replace(/(\d)\.(\d)/g, '$1$2.')).fullStr
}

const getDecimalValue = (
  stringMode: boolean | undefined,
  decimalValue: DecimalClass
): ValueType =>
  stringMode || decimalValue.isEmpty()
    ? decimalValue.toString()
    : decimalValue.toNumber()

const getDecimalIfValidate = (value: ValueType | undefined | null) => {
  if (value === undefined || value === null) return null
  const decimal = getMiniDecimal(value)
  return decimal.isInvalidate() ? null : decimal
}

/** rc-util's `useLayoutUpdateEffect`: a layout effect that skips mount. */
const useLayoutUpdateEffect: typeof React.useEffect = (callback, deps) => {
  const firstMountRef = React.useRef(true)
  // useLayoutEffect, as rc-util does in a browser: the input's text must be
  // re-synced before paint, or a controlled call site flashes the old string.
  React.useLayoutEffect(() => {
    if (firstMountRef.current) return undefined
    return callback()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
  React.useLayoutEffect(() => {
    firstMountRef.current = false
    return () => {
      firstMountRef.current = true
    }
  }, [])
}

/** rc-input-number/es/hooks/useFrame.ts — latest-wins on a frame. */
const useFrame = () => {
  const idRef = React.useRef(0)
  React.useEffect(
    () => () => {
      cancelAnimationFrame(idRef.current)
    },
    []
  )
  return (callback: () => void) => {
    cancelAnimationFrame(idRef.current)
    idRef.current = requestAnimationFrame(callback)
  }
}

/**
 * rc-input-number/es/hooks/useCursor.ts — keep the caret where the reader put
 * it when a `formatter` rewrites the text under them.
 */
const useCursor = (
  input: HTMLInputElement | null,
  focused: boolean
): [() => void, () => void] => {
  const selectionRef = React.useRef<{
    start: number | null
    beforeTxt: string
    afterTxt: string
  } | null>(null)

  const recordCursor = () => {
    try {
      if (!input) return
      const { selectionStart: start, selectionEnd: end, value } = input
      selectionRef.current = {
        start,
        beforeTxt: value.substring(0, start ?? 0),
        afterTxt: value.substring(end ?? 0),
      }
    } catch {
      // Chrome throws reading selectionStart on some input types; rc swallows
      // this too — a lost caret position is not worth an error boundary.
    }
  }

  const restoreCursor = () => {
    if (!input || !selectionRef.current || !focused) return
    try {
      const { value } = input
      const { beforeTxt, afterTxt, start } = selectionRef.current
      let startPos = value.length
      if (value.endsWith(afterTxt)) startPos = value.length - afterTxt.length
      else if (value.startsWith(beforeTxt)) startPos = beforeTxt.length
      else {
        const beforeLastChar = beforeTxt[(start ?? 0) - 1]
        const newIndex = value.indexOf(beforeLastChar, (start ?? 0) - 1)
        if (newIndex !== -1) startPos = newIndex + 1
      }
      input.setSelectionRange(startPos, startPos)
    } catch {
      // As above.
    }
  }

  return [recordCursor, restoreCursor]
}

export interface NumericMachineOptions {
  value?: ValueType | null
  defaultValue?: ValueType | null
  min?: ValueType
  max?: ValueType
  step?: ValueType
  precision?: number
  decimalSeparator?: string
  stringMode?: boolean
  disabled?: boolean
  readOnly?: boolean
  keyboard?: boolean
  changeOnBlur?: boolean
  changeOnWheel?: boolean
  formatter?: (
    value: string | undefined,
    info: { userTyping: boolean; input: string }
  ) => string
  parser?: (displayValue: string | undefined) => ValueType
  onChange?: (value: ValueType | null) => void
  onInput?: (text: string) => void
  onPressEnter?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onStep?: (
    value: ValueType,
    info: { offset: ValueType; type: 'up' | 'down' }
  ) => void
  inputRef: React.RefObject<HTMLInputElement>
}

export interface NumericMachine {
  inputValue: string
  upDisabled: boolean
  downDisabled: boolean
  outOfRange: boolean
  ariaValueNow: string | undefined
  doStep: (up: boolean) => void
  handlers: {
    onChange: React.ChangeEventHandler<HTMLInputElement>
    onKeyDown: React.KeyboardEventHandler<HTMLInputElement>
    onKeyUp: React.KeyboardEventHandler<HTMLInputElement>
    onFocus: React.FocusEventHandler<HTMLInputElement>
    onBlur: React.FocusEventHandler<HTMLInputElement>
    onCompositionStart: React.CompositionEventHandler<HTMLInputElement>
    onCompositionEnd: React.CompositionEventHandler<HTMLInputElement>
    onBeforeInput: React.FormEventHandler<HTMLInputElement>
  }
}

/**
 * rc-input-number's `InternalInputNumber` with the rendering removed. Returns
 * the text to display, the two stepper states, and the handlers to hang on
 * the `<input>`.
 */
export const useNumericMachine = ({
  value,
  defaultValue,
  min,
  max,
  step = 1,
  precision,
  decimalSeparator,
  stringMode,
  disabled,
  readOnly,
  keyboard,
  changeOnBlur = true,
  changeOnWheel = false,
  formatter,
  parser,
  onChange,
  onInput,
  onPressEnter,
  onStep,
  inputRef,
}: NumericMachineOptions): NumericMachine => {
  const [focused, setFocus] = React.useState(false)
  const userTypingRef = React.useRef(false)
  const compositionRef = React.useRef(false)
  const shiftKeyRef = React.useRef(false)

  // ============================ Value =============================
  const [decimalValue, setDecimalValue] = React.useState<DecimalClass>(() =>
    getMiniDecimal(value ?? defaultValue ?? '')
  )

  const setUncontrolledDecimalValue = (newDecimal: DecimalClass) => {
    if (value === undefined) setDecimalValue(newDecimal)
  }

  // ====================== Parser & Formatter ======================
  /**
   * `precision` drives the formatter and `onChange`. It is auto-derived from
   * `value` and `step` when the call site gives none — legacy rc behaviour,
   * and 141 of this app's tags depend on it (they pass `step` but no
   * `precision`). It must NOT apply while the reader is mid-keystroke, or
   * `1.` collapses to `1` before the second digit arrives.
   */
  const getPrecision = React.useCallback(
    (numStr: string, userTyping: boolean) => {
      if (userTyping) return undefined
      if (precision !== undefined && precision >= 0) return precision
      return Math.max(getNumberPrecision(numStr), getNumberPrecision(step))
    },
    [precision, step]
  )

  const mergedParser = React.useCallback(
    (num: string | number) => {
      const numStr = String(num)
      if (parser) return parser(numStr)
      let parsedStr = numStr
      if (decimalSeparator) parsedStr = parsedStr.replace(decimalSeparator, '.')
      // [Legacy, rc's word] still converts `$ 123,456` to `123456`.
      return parsedStr.replace(/[^\w.-]+/g, '')
    },
    [parser, decimalSeparator]
  )

  const inputValueRef = React.useRef('')

  const mergedFormatter = React.useCallback(
    (number: string | number, userTyping: boolean) => {
      if (formatter)
        return formatter(String(number), {
          userTyping,
          input: String(inputValueRef.current),
        })

      let str = typeof number === 'number' ? num2str(number) : number
      if (!userTyping) {
        const mergedPrecision = getPrecision(str, userTyping)
        if (
          validateNumber(str) &&
          (decimalSeparator || (mergedPrecision ?? -1) >= 0)
        ) {
          const separatorStr = decimalSeparator || '.'
          str = toFixed(str, separatorStr, mergedPrecision)
        }
      }
      return str
    },
    [formatter, getPrecision, decimalSeparator]
  )

  // ========================== InputValue ==========================
  const [inputValue, setInternalInputValue] = React.useState<string | number>(
    () => {
      const initValue = defaultValue ?? value
      if (
        decimalValue.isInvalidate() &&
        ['string', 'number'].includes(typeof initValue)
      ) {
        return Number.isNaN(initValue) ? '' : (initValue as string | number)
      }
      return mergedFormatter(decimalValue.toString(), false)
    }
  )
  inputValueRef.current = inputValue as string

  const setInputValue = (newValue: DecimalClass, userTyping: boolean) => {
    setInternalInputValue(
      mergedFormatter(
        // An invalid number can be handed in by an external control; let it
        // through rather than blanking what the caller asked to display.
        newValue.isInvalidate()
          ? newValue.toString(false)
          : newValue.toString(!userTyping),
        userTyping
      )
    )
  }

  // >>> Max & Min limit
  const maxDecimal = React.useMemo(
    () => getDecimalIfValidate(max),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [max, precision]
  )
  const minDecimal = React.useMemo(
    () => getDecimalIfValidate(min),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [min, precision]
  )

  const upDisabled = React.useMemo(() => {
    if (!maxDecimal || !decimalValue || decimalValue.isInvalidate())
      return false
    return maxDecimal.lessEquals(decimalValue)
  }, [maxDecimal, decimalValue])

  const downDisabled = React.useMemo(() => {
    if (!minDecimal || !decimalValue || decimalValue.isInvalidate())
      return false
    return decimalValue.lessEquals(minDecimal)
  }, [minDecimal, decimalValue])

  const [recordCursor, restoreCursor] = useCursor(inputRef.current, focused)

  // ============================= Data =============================
  /** Closest value inside [min, max], or null when already inside. */
  const getRangeValue = (target: DecimalClass) => {
    if (maxDecimal && !target.lessEquals(maxDecimal)) return maxDecimal
    if (minDecimal && !minDecimal.lessEquals(target)) return minDecimal
    return null
  }

  const isInRange = (target: DecimalClass) => !getRangeValue(target)

  /**
   * Emit `onChange` if the value is valid and actually changed, and return the
   * value re-aligned into range.
   */
  const triggerValueUpdate = (
    newValue: DecimalClass,
    userTyping: boolean
  ): DecimalClass => {
    let updateValue = newValue
    let isRangeValidate = isInRange(updateValue) || updateValue.isEmpty()

    // Clamping while the reader is still typing would fight them: `1` on its
    // way to `15` is below a min of 10. Clamp on flush, not on keystroke.
    if (!updateValue.isEmpty() && !userTyping) {
      updateValue = getRangeValue(updateValue) || updateValue
      isRangeValidate = true
    }

    if (!readOnly && !disabled && isRangeValidate) {
      const numStr = updateValue.toString()
      const mergedPrecision = getPrecision(numStr, userTyping)
      if ((mergedPrecision ?? -1) >= 0) {
        updateValue = getMiniDecimal(toFixed(numStr, '.', mergedPrecision))
        // Rounding can push the value back out of range: 4 in [0, 3.8]
        // rounds to 4 again, so cut instead of rounding that once.
        if (!isInRange(updateValue)) {
          updateValue = getMiniDecimal(
            toFixed(numStr, '.', mergedPrecision, true)
          )
        }
      }

      if (!updateValue.equals(decimalValue)) {
        setUncontrolledDecimalValue(updateValue)
        onChange?.(
          updateValue.isEmpty()
            ? null
            : getDecimalValue(stringMode, updateValue)
        )
        if (value === undefined) setInputValue(updateValue, userTyping)
      }
      return updateValue
    }
    return decimalValue
  }

  // ========================== User Input ==========================
  const onNextPromise = useFrame()

  const collectInputValue = (inputStr: string) => {
    recordCursor()

    inputValueRef.current = inputStr
    setInternalInputValue(inputStr)

    if (!compositionRef.current) {
      const finalValue = mergedParser(inputStr)
      const finalDecimal = getMiniDecimal(finalValue)
      if (!finalDecimal.isNaN()) triggerValueUpdate(finalDecimal, true)
    }

    onInput?.(inputStr)

    // Chinese IME: a full-width `。` is a decimal point.
    onNextPromise(() => {
      let nextInputStr = inputStr
      if (!parser) nextInputStr = inputStr.replace(/。/g, '.')
      if (nextInputStr !== inputStr) collectInputValue(nextInputStr)
    })
  }

  // ============================= Step =============================
  const doStep = (up: boolean) => {
    if ((up && upDisabled) || (!up && downDisabled)) return

    // A step is not typing, even when it came from the arrow keys.
    userTypingRef.current = false
    let stepDecimal = getMiniDecimal(
      shiftKeyRef.current ? getDecupleSteps(step) : step
    )
    if (!up) stepDecimal = stepDecimal.negate()

    const target = (decimalValue || getMiniDecimal(0)).add(
      stepDecimal.toString()
    )
    const updatedValue = triggerValueUpdate(target, false)
    onStep?.(getDecimalValue(stringMode, updatedValue), {
      offset: shiftKeyRef.current ? getDecupleSteps(step) : step,
      type: up ? 'up' : 'down',
    })
    inputRef.current?.focus()
  }

  // ============================ Flush =============================
  /**
   * Push the box's text through the value pipeline and re-render it. An
   * unparseable text falls back to the last valid value rather than to zero.
   */
  const flushInputValue = (userTyping: boolean) => {
    const parsedValue = getMiniDecimal(mergedParser(inputValue as string))
    let formatValue = parsedValue
    if (!parsedValue.isNaN())
      formatValue = triggerValueUpdate(parsedValue, userTyping)
    else formatValue = triggerValueUpdate(decimalValue, userTyping)

    if (value !== undefined) setInputValue(decimalValue, false)
    else if (!formatValue.isNaN()) setInputValue(formatValue, false)
  }

  const onInternalInput: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    collectInputValue(e.target.value)
  }

  const onBeforeInput = () => {
    userTypingRef.current = true
  }

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    const { key, shiftKey } = event
    userTypingRef.current = true
    shiftKeyRef.current = shiftKey

    if (key === 'Enter') {
      if (!compositionRef.current) userTypingRef.current = false
      flushInputValue(false)
      onPressEnter?.(event)
    }

    if (keyboard === false) return

    if (
      !compositionRef.current &&
      ['Up', 'ArrowUp', 'Down', 'ArrowDown'].includes(key)
    ) {
      doStep(key === 'Up' || key === 'ArrowUp')
      event.preventDefault()
    }
  }

  const onKeyUp = () => {
    userTypingRef.current = false
    shiftKeyRef.current = false
  }

  const onCompositionStart = () => {
    compositionRef.current = true
  }

  const onCompositionEnd = () => {
    compositionRef.current = false
    collectInputValue(inputRef.current?.value ?? '')
  }

  const onFocus = () => {
    setFocus(true)
  }

  const onBlur = () => {
    if (changeOnBlur) flushInputValue(false)
    setFocus(false)
    userTypingRef.current = false
  }

  // A React `onWheel` is passive, so `preventDefault()` needs a DOM listener.
  React.useEffect(() => {
    if (!changeOnWheel || !focused) return undefined
    const input = inputRef.current
    if (!input) return undefined
    const onWheel = (event: WheelEvent) => {
      doStep(event.deltaY < 0)
      event.preventDefault()
    }
    input.addEventListener('wheel', onWheel, { passive: false })
    return () => input.removeEventListener('wheel', onWheel)
  })

  // ========================== Controlled ==========================
  useLayoutUpdateEffect(() => {
    if (!decimalValue.isInvalidate()) setInputValue(decimalValue, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [precision, formatter])

  useLayoutUpdateEffect(() => {
    const newValue = getMiniDecimal(value ?? '')
    setDecimalValue(newValue)
    const currentParsedValue = getMiniDecimal(
      mergedParser(inputValue as string)
    )

    // `1.2` on its way to `1.` must not snap back to `1` — unless a
    // `formatter` owns the text, in which case it decides.
    if (
      !newValue.equals(currentParsedValue) ||
      !userTypingRef.current ||
      formatter
    ) {
      setInputValue(newValue, userTypingRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useLayoutUpdateEffect(() => {
    if (formatter) restoreCursor()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue])

  return {
    inputValue: String(inputValue ?? ''),
    upDisabled,
    downDisabled,
    // rc-input-number's own `${prefixCls}-out-of-range` predicate, verbatim:
    // a CONTROLLED value outside min/max is FLAGGED rather than clamped,
    // because clamping would overwrite what the call site asked for. The
    // seam paints it; `./InputNumber.shadcn.tsx` says how.
    outOfRange: !decimalValue.isInvalidate() && !isInRange(decimalValue),
    ariaValueNow: decimalValue.isInvalidate()
      ? undefined
      : decimalValue.toString(),
    doStep,
    handlers: {
      onChange: onInternalInput,
      onKeyDown,
      onKeyUp,
      onFocus,
      onBlur,
      onCompositionStart,
      onCompositionEnd,
      onBeforeInput,
    },
  }
}
